import React, { ReactNode } from 'react';

import { Filter, FilterIDs, FilterService, FilterValue } from './filter.types';
import { AppRoutes } from '../../app/routes/utils';
import { getFilterParams } from './filter.utils';

export const FilterContext = React.createContext<FilterService | null>(null);

function cloneFilters(filters: Filter[]) {
  return filters.map((f) => f.copy());
}

interface Props {
  children: ReactNode;
}

// TODO: Extend to support active and deactivate
export const FilterProvider: React.FC<Props> = ({ children }) => {
  const [filters, setFilters] = React.useState<Filter[]>([]);

  const setFiltersWrapper = React.useCallback((filterID: FilterIDs, filters: Filter[]) => {
    setFilters(filters);
  }, []);

  const addFilter = React.useCallback(
    (
      filterID: FilterIDs,
      filterName: string,
      filterText: string,
      value: FilterValue,
      type: string
    ) => {
      const filterCopy = cloneFilters(filters);
      const foundFilter = filterCopy.find(
        ({ target, name }) => target === filterID && name === filterName
      );
      if (foundFilter) foundFilter.value = value;
      else filterCopy.push(new Filter(filterID, filterName, filterText, value, true, type));
      setFiltersWrapper(filterID, filterCopy);
    },
    [filters, setFiltersWrapper]
  );

  const updateFilters = React.useCallback(
    (filterID: FilterIDs, changes: Filter[]) => {
      const filterCopy = cloneFilters(filters);
      // Update or append new filter values
      changes.forEach((ft) => {
        const { name: filterName } = ft;
        const foundIndex = filterCopy.findIndex(
          ({ target, name }) => target === filterID && name === filterName
        );
        if (foundIndex !== -1) {
          filterCopy[foundIndex] = ft.copy();
        } else {
          filterCopy.push(ft.copy());
        }
      });
      // Clear removed filters
      const newFilters = filterCopy.filter(
        ({ value }) =>
          (value !== undefined && !Array.isArray(value)) ||
          (Array.isArray(value) && value.length > 0)
      );
      setFiltersWrapper(filterID, newFilters);
    },
    [filters, setFiltersWrapper]
  );

  const clearFilters = React.useCallback(
    (filterID: FilterIDs) => {
      const newFilters = filters.filter(({ target }) => target !== filterID);
      setFiltersWrapper(filterID, newFilters);
    },
    [filters, setFiltersWrapper]
  );

  const removeFilter = React.useCallback(
    (filterID: FilterIDs, filterName: string) => {
      const newFilters = filters.filter(
        ({ target, name }) => !(target === filterID && name === filterName)
      );
      setFiltersWrapper(filterID, newFilters);
    },
    [filters, setFiltersWrapper]
  );

  const toggleFilter = React.useCallback(
    (filterID: FilterIDs, filterName: string) => {
      const newFilters = cloneFilters(filters);
      const index = newFilters.findIndex(({ name }) => name === filterName);
      if (index > -1) {
        newFilters[index].active = !newFilters[index].active;
        setFiltersWrapper(filterID, newFilters);
      }
    },
    [filters, setFiltersWrapper]
  );

  const getFilters = React.useCallback(
    (filterID: FilterIDs) => filters.filter(({ target }) => target === filterID),
    [filters]
  );

  const getFiltersAsURLParams = React.useCallback(
    (filterID: FilterIDs) => {
      const urlFilters = filters.filter(({ active, target }) => active && target === filterID);
      const filterParamsArr = getFilterParams(urlFilters);
      return Object.fromEntries(new URLSearchParams(filterParamsArr).entries());
    },
    [filters]
  );

  const getFilterIDForRoute = React.useCallback((pathname: string) => {
    switch (pathname) {
      case AppRoutes.Users:
        return FilterIDs.UserFilters;
      case AppRoutes.Requests:
        return FilterIDs.RequestFilters;
      default:
        return null;
    }
  }, []);

  const filterService = React.useMemo(
    () => ({
      getFilters,
      getFiltersAsURLParams,
      addFilter,
      clearFilters,
      removeFilter,
      toggleFilter,
      updateFilters,
      getFilterIDForRoute,
    }),
    [
      getFilters,
      getFiltersAsURLParams,
      addFilter,
      clearFilters,
      removeFilter,
      toggleFilter,
      updateFilters,
      getFilterIDForRoute,
    ]
  );

  return <FilterContext.Provider value={filterService}>{children}</FilterContext.Provider>;
};

export function useFilterService() {
  const filterService = React.useContext(FilterContext);
  if (!filterService) throw new Error('useFilterService must be used within FilterProvider');
  return filterService;
}

export function useFilter(filterID: FilterIDs) {
  const filterService = React.useContext(FilterContext);
  if (!filterService) throw new Error('useFilter must be used within FilterProvider');
  return filterService.getFilters(filterID);
}

export function useFilterAsURL(filterID: FilterIDs) {
  const filterService = React.useContext(FilterContext);
  if (!filterService) throw new Error('useFilterAsURL must be used within FilterProvider');
  return filterService.getFiltersAsURLParams(filterID);
}
